import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import LiveResultsContext from './features/results/context/LiveResultsContext';
import { useParams } from 'react-router-dom';
import Navbar from './Navbar';
import FooterPart from './Footer';
import useMobileCheck from './useMobileCheck';
import DesktopResults from './features/results/components/DesktopResults';
import ResultsFilters from './features/results/components/ResultsFilters';
import StageLeaderboard from './features/results/components/mobile/StageLeaderboard';
import TestLeaderboard from './features/results/components/mobile/TestLeaderboard';
import useCompetitionDetails from './features/results/hooks/useCompetitionDetails';
import usePersistedSelection from './features/results/hooks/usePersistedSelection';
import {
  buildGroupOptions,
  deriveSelectionState,
  matchGroupValue,
} from './features/results/utils/selection';
import LiveResultsProvider from './features/results/context/LiveResultsProvider';

const ResultsInner = ({ numericEventID }) => {
  const isMobile = useMobileCheck();
  const { data: competitions, loading: competitionsLoading } =
    useCompetitionDetails(numericEventID);
  const { selection, updateSelection, setGroup } =
    usePersistedSelection(numericEventID);
  const { getStageTimingPhase, stagePhases } = useContext(LiveResultsContext) || {};

  const [filterIndexes, setFilterIndexes] = useState({
    competition: 0,
    classification: 0,
    stage: 0,
  });
  const [groupOptions, setGroupOptions] = useState([]);
  const [viewMode, setViewMode] = useState('odcinkowa');

  const resolvedSelection = useMemo(
    () => deriveSelectionState(competitions, selection),
    [competitions, selection]
  );

  useEffect(() => {
    if (!resolvedSelection) {
      setGroupOptions([]);
      return;
    }

    setFilterIndexes({
      competition: resolvedSelection.competitionIndex,
      classification: resolvedSelection.classificationIndex,
      stage: resolvedSelection.stageIndex,
    });
    setGroupOptions(resolvedSelection.groupOptions);
    updateSelection(resolvedSelection.selectionPayload);
  }, [resolvedSelection, updateSelection]);

  const handleCompetitionChange = useCallback(
    (competitionIndex) => {
      const competition = competitions[competitionIndex];
      if (!competition) {
        return;
      }

      const classificationIndex = 0;
      const stageIndex = 0;
      const classification = competition.Classifications?.[classificationIndex];
      const stage = competition.Stages?.[stageIndex];
      const options = buildGroupOptions(classification);
      const groupValue = matchGroupValue(options, selection.group);

      setFilterIndexes({
        competition: competitionIndex,
        classification: classificationIndex,
        stage: stageIndex,
      });
      setGroupOptions(options);
      setViewMode('odcinkowa');
      updateSelection({
        competitionId: competition.IDCompetition,
        competitionName: competition.NameCompetition,
        classificationId: classification?.ClassificationID || null,
        stageId: stage?.StageID || null,
        stageStatus: stage?.StageStatus || null,
        stageStatusDesc: stage?.StageStatusDesc || '',
        stageIsTested: stage?.IsTested || 0,
        timingPhase: stage?.TimingPhase || 'NOT_STARTED',
        group: groupValue,
      });
    },
    [competitions, selection.group, updateSelection]
  );

  const handleClassificationChange = useCallback(
    (classificationIndex) => {
      const competition = competitions[filterIndexes.competition];
      const classification = competition?.Classifications?.[classificationIndex];

      if (!classification) {
        return;
      }

      const options = buildGroupOptions(classification);
      const groupValue = matchGroupValue(options, selection.group);

      setFilterIndexes((prev) => ({ ...prev, classification: classificationIndex }));
      setGroupOptions(options);
      setViewMode('odcinkowa');
      updateSelection({
        classificationId: classification.ClassificationID,
        group: groupValue,
      });
    },
    [competitions, filterIndexes.competition, selection.group, updateSelection]
  );

  const handleGroupChange = useCallback(
    (groupValue) => {
      setGroup(groupValue);
    },
    [setGroup]
  );

  const handleStageChange = useCallback(
    (stageIndex) => {
      const competition = competitions[filterIndexes.competition];
      const stage = competition?.Stages?.[stageIndex];

      if (!stage) {
        return;
      }

      setFilterIndexes((prev) => ({ ...prev, stage: stageIndex }));
      setViewMode('odcinkowa');
      updateSelection({
        stageId: stage.StageID,
        stageStatus: stage.StageStatus,
        stageStatusDesc: stage.StageStatusDesc,
        stageIsTested: stage.IsTested,
        timingPhase: stage.TimingPhase || 'NOT_STARTED',
      });
    },
    [competitions, filterIndexes.competition, updateSelection]
  );

  // Żywa faza wybranego odcinka liczona przy renderze — bez efektu, bez
  // zapisywania do selection (WS bootstrap + STAGE_PHASE_UPDATE zawsze wygrywa
  // ze snapshotem REST z chwili wczytania strony).
  const liveTimingPhase =
    (getStageTimingPhase && selection.stageId
      ? getStageTimingPhase(selection.competitionId, selection.stageId)
      : null) ?? selection.timingPhase;

  const shouldShowLoading = competitionsLoading || !resolvedSelection;
  const hasCompetitions = competitions.length > 0;

  return (
    <div className="container-bkg">
      <Navbar EventID={numericEventID} />
      <div className="container is-fluid mt-1">
        <div className="mt-3">
          {hasCompetitions ? (
            <ResultsFilters
              competitions={competitions}
              competitionIndex={filterIndexes.competition}
              classificationIndex={filterIndexes.classification}
              stageIndex={filterIndexes.stage}
              groupOptions={groupOptions}
              selectedGroupValue={selection.group}
              onCompetitionChange={handleCompetitionChange}
              onClassificationChange={handleClassificationChange}
              onGroupChange={handleGroupChange}
              onStageChange={handleStageChange}
              isMobile={isMobile}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              stagePhases={stagePhases}
            />
          ) : (
            <div className="notification has-text-centered">Brak danych</div>
          )}
        </div>

        {shouldShowLoading ? (
          <div>Loading...</div>
        ) : isMobile ? (
          selection.stageIsTested ? (
            <TestLeaderboard
              selectedEventID={numericEventID}
              selectedCompetitionID={selection.competitionId}
              selectedStageID={selection.stageId}
              selectedClassificationID={selection.classificationId}
              selectedGroupID={selection.group}
              stageStatus={selection.stageStatus}
              stageStatusDesc={selection.stageStatusDesc}
            />
          ) : (
            <StageLeaderboard
              selectedEventID={numericEventID}
              selectedCompetitionID={selection.competitionId}
              selectedStageID={selection.stageId}
              selectedClassificationID={selection.classificationId}
              selectedGroupID={selection.group}
              stageStatus={selection.stageStatus}
              stageStatusDesc={selection.stageStatusDesc}
              timingPhase={liveTimingPhase}
              viewMode={viewMode}
            />
          )
        ) : (
          <DesktopResults
            selectedCompetition={selection.competitionName}
            selectedEventID={numericEventID}
            selectedCompetitionID={selection.competitionId}
            selectedGroupID={selection.group}
            selectedStageID={selection.stageId}
            stageStatusDesc={selection.stageStatusDesc}
            selectedStageStatus={selection.stageStatus}
            selectedClassificationID={selection.classificationId}
            stageIsTested={selection.stageIsTested}
            timingPhase={liveTimingPhase}
            selectedGroupList={groupOptions}
            onGroupChange={handleGroupChange}
          />
        )}
      </div>
      <div className="section">
        <FooterPart />
      </div>
    </div>
  );
};

const Results = () => {
  const { EventID } = useParams();
  const numericEventID = EventID.replace('event-', '');
  return (
    <LiveResultsProvider eventId={numericEventID}>
      <ResultsInner numericEventID={numericEventID} />
    </LiveResultsProvider>
  );
};

export default Results;
